import type { SkillId } from '../../shared/types/ids';
import type { SkillEstimate, SkillEvidence } from '../../shared/types/skills';

/** Evidence count at which an estimate is treated as fully confident. */
export const CONFIDENT_EVIDENCE_COUNT = 5;

/** Below this detector confidence a non-correct result is an input problem, not a mistake. */
export const LOW_INPUT_CONFIDENCE_THRESHOLD = 0.5;

const RESULT_WEIGHT = { correct: 1, assisted: 0.5, incorrect: 0 } as const;

/**
 * Whether a record may move a skill estimate. Non-responses never count against a child,
 * and a low-confidence detection is never an academic failure (architecture §12, §15).
 */
export function isAcademicEvidence(evidence: SkillEvidence): boolean {
  if (evidence.result === 'skipped') return false;
  if (evidence.result === 'correct') return true;
  return (evidence.inputConfidence ?? 1) >= LOW_INPUT_CONFIDENCE_THRESHOLD;
}

export interface CreateSkillEvidenceInput extends Omit<SkillEvidence, 'recordedAt'> {
  recordedAt?: string;
}

export function createSkillEvidence(
  input: CreateSkillEvidenceInput,
  now: () => Date = () => new Date(),
): SkillEvidence {
  const { recordedAt, ...rest } = input;
  return { ...rest, recordedAt: recordedAt ?? now().toISOString() };
}

/** Collapses raw evidence into one estimate per skill. Pure; safe to recompute at any time. */
export function summariseSkillEvidence(evidence: readonly SkillEvidence[]): SkillEstimate[] {
  const grouped = new Map<SkillId, SkillEvidence[]>();
  for (const item of evidence) {
    const bucket = grouped.get(item.skillId);
    if (bucket) bucket.push(item);
    else grouped.set(item.skillId, [item]);
  }

  const estimates: SkillEstimate[] = [];
  for (const [skillId, items] of grouped) {
    const scored = items.filter(isAcademicEvidence);
    const weighted = scored.reduce(
      (total, item) => total + (RESULT_WEIGHT[item.result as keyof typeof RESULT_WEIGHT] ?? 0),
      0,
    );
    const lastPractisedAt = items.reduce<string | undefined>(
      (latest, item) =>
        latest === undefined || item.recordedAt > latest ? item.recordedAt : latest,
      undefined,
    );

    const estimate: SkillEstimate = {
      skillId,
      level: scored.length === 0 ? 0 : weighted / scored.length,
      confidence: Math.min(1, scored.length / CONFIDENT_EVIDENCE_COUNT),
      evidenceCount: scored.length,
    };
    estimates.push(lastPractisedAt === undefined ? estimate : { ...estimate, lastPractisedAt });
  }

  return estimates;
}
